// POST /api/auth/password-reset/request — the self-service "I forgot my
// password" entry point that the login UI's "Forgot password?" link calls.
//
// The endpoint always answers `sent: true` regardless of whether the email
// is on file (anti-enumeration), so the hook just fires the request and
// surfaces the toast. Errors at the transport layer — a 500 from Brevo,
// a network blip — are caught and surfaced as the same toast the caller
// already gets for any login failure. Anything else propagates so an
// upstream layer can log it.
//
// The endpoint contract is documented at the route in
// `src/routes/auth_routes.py::request_password_reset` and locked in by
// `src/tests/api/test_password_reset_request.py`.

import useFetch from "../useFetch";
import type { Platform } from "@/utils/paths";

// `login_path_prefix` is the login surface the request comes from: the email
// resolves to the same identity (staff or entrepreneur) that logging in there
// would. `platform` brands the email and picks the link's host.
export interface PasswordResetRequestPayload {
  email: string;
  login_path_prefix: string;
  platform: Platform | null;
}

export interface PasswordResetRequestResult {
  sent: boolean;
}

export function useRequestPasswordReset() {
  const { doFetch } = useFetch();

  return {
    request: async (
      payload: PasswordResetRequestPayload
    ): Promise<PasswordResetRequestResult> => {
      return doFetch<PasswordResetRequestResult>(
        "/auth/password-reset/request",
        {
          method: "POST",
          data: payload,
        }
      );
    },
  };
}
